import { FormEvent, useEffect, useMemo, useState } from 'react';
import { fetchBoards } from '../../api/board';
import {
  createContract,
  fetchContract,
  updateContract,
} from '../../api/contract';
import {reportError} from '../../utils/notify';
import { Board } from '../../types/board';
import {
  calcItemAmount,
  CONTRACT_UNITS,
  Contract,
  ContractItem,
  ContractPayload,
  ContractStage,
  createDefaultContractStages,
  createEmptyContractItem,
  isWritingStage,
} from '../../types/contract';
import { Product } from '../../types/product';
import {
  formatUnitPriceDisplay,
  formatUnitPriceInput,
  parseUnitPriceInput,
} from '../../types/purchaseOrder';
import { formatNumberDisplay, formatNumberInput, parseNumberInput } from '../../utils/numberFormat';
import ContractComments from './ContractComments';
import ContractProductCell, { loadContractProducts } from './ContractProductCell';
import ContractStageEditor from './ContractStageEditor';
import ContractStageEmbed from './ContractStageEmbed';
import ContractStageStepper from './ContractStageStepper';
import './ContractForm.css';

const EMPTY_FORM: ContractPayload = {
  contractNo: '',
  contractName: '',
  clientName: '',
  clientManagerName: '',
  clientManagerPhone: '',
  startDate: '',
  endDate: '',
  deliveryDueDate: '',
  managerName: '',
  deliveryPlace: '',
  deliveryManagerName: '',
  deliveryManagerPhone: '',
  currentStage: '작성',
  currentStageId: null,
  stages: createDefaultContractStages(),
  urgent: false,
  items: [createEmptyContractItem()],
};

interface ContractFormProps {
  mode: 'create' | 'edit';
  contractId?: number;
  onCancel: () => void;
  onSaved: (contract: Contract) => void;
}

function ContractForm({ mode, contractId, onCancel, onSaved }: ContractFormProps) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState<ContractPayload>({
    ...EMPTY_FORM,
    items: [createEmptyContractItem()],
    stages: createDefaultContractStages(),
  });
  const [documentNo, setDocumentNo] = useState('');
  const [boards, setBoards] = useState<Board[]>([]);
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const [editingStages, setEditingStages] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState<Product[]>([]);

  const stages = form.stages ?? createDefaultContractStages();
  const activeStage = stages[activeStageIndex] ?? stages[0];
  const showWritingBody = isWritingStage(activeStage);

  useEffect(() => {
    void fetchBoards()
      .then(setBoards)
      .catch(() => setBoards([]));
    void loadContractProducts()
      .then(setProducts)
      .catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    if (!isEdit || contractId == null) {
      setForm({
        ...EMPTY_FORM,
        items: [createEmptyContractItem()],
        stages: createDefaultContractStages(),
      });
      setDocumentNo('');
      setActiveStageIndex(0);
      setEditingStages(false);
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const contract = await fetchContract(contractId);
        const items =
          contract.items && contract.items.length > 0
            ? contract.items.map((item) => ({
                contractItemId: item.contractItemId,
                productId: item.productId ?? null,
                productName: item.productName,
                unit: item.unit || 'EA',
                quantity: item.quantity ?? 1,
                unitPrice: item.unitPrice ?? null,
                sortOrder: item.sortOrder,
              }))
            : [createEmptyContractItem()];
        const nextStages =
          contract.stages && contract.stages.length > 0
            ? contract.stages
            : createDefaultContractStages();
        const currentIndex = Math.max(
          0,
          nextStages.findIndex(
            (stage) =>
              (contract.currentStageId != null && stage.stageId === contract.currentStageId) ||
              stage.stageName === contract.currentStage
          )
        );

        setForm({
          contractNo: contract.contractNo ?? '',
          contractName: contract.contractName === '(임시저장)' ? '' : contract.contractName,
          clientName: contract.clientName ?? '',
          clientManagerName: contract.clientManagerName ?? '',
          clientManagerPhone: contract.clientManagerPhone ?? '',
          startDate: contract.startDate ?? '',
          endDate: contract.endDate ?? '',
          deliveryDueDate: contract.deliveryDueDate ?? '',
          managerName: contract.managerName ?? '',
          deliveryPlace: contract.deliveryPlace ?? '',
          deliveryManagerName: contract.deliveryManagerName ?? '',
          deliveryManagerPhone: contract.deliveryManagerPhone ?? '',
          currentStage: contract.currentStage ?? '작성',
          currentStageId: contract.currentStageId ?? null,
          stages: nextStages,
          urgent: Boolean(contract.urgent),
          items,
        });
        setDocumentNo(contract.documentNo ?? '');
        setActiveStageIndex(currentIndex === -1 ? 0 : currentIndex);
        setError('');
      } catch {
        reportError(setError, '계약서를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [isEdit, contractId]);

  const items = useMemo(() => form.items ?? [], [form.items]);
  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + calcItemAmount(item), 0),
    [items]
  );

  const updateItem = (index: number, patch: Partial<ContractItem>) => {
    setForm((prev) => {
      const nextItems = [...(prev.items ?? [])];
      nextItems[index] = { ...nextItems[index], ...patch };
      return { ...prev, items: nextItems };
    });
  };

  const applyProductToItem = (index: number, product: Product) => {
    updateItem(index, {
      productId: product.productId,
      productName: product.productName,
      unit: product.unit || 'EA',
      unitPrice: product.supplyPrice ?? 0,
    });
  };

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...(prev.items ?? []), createEmptyContractItem()],
    }));
  };

  const removeItem = (index: number) => {
    setForm((prev) => {
      const nextItems = [...(prev.items ?? [])];
      if (nextItems.length <= 1) {
        return { ...prev, items: [createEmptyContractItem()] };
      }
      nextItems.splice(index, 1);
      return { ...prev, items: nextItems };
    });
  };

  const buildPayload = (draft: boolean): ContractPayload | null => {
    const contractName = form.contractName.trim();
    if (!draft && !contractName) {
      reportError(setError, '계약명을 입력해 주세요.');
      return null;
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      reportError(setError, '계약 종료일은 시작일 이후여야 합니다.');
      return null;
    }

    const payloadItems = (form.items ?? [])
      .map((item) => ({
        productId: item.productId ?? undefined,
        productName: item.productName.trim(),
        unit: item.unit?.trim() || 'EA',
        quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1,
        unitPrice:
          item.unitPrice == null || Number.isNaN(Number(item.unitPrice))
            ? 0
            : Math.max(0, Number(item.unitPrice)),
      }))
      .filter((item) => item.productName.length > 0);

    return {
      contractNo: form.contractNo?.trim() || undefined,
      contractName,
      clientName: form.clientName?.trim() || undefined,
      clientManagerName: form.clientManagerName?.trim() || undefined,
      clientManagerPhone: form.clientManagerPhone?.trim() || undefined,
      startDate: form.startDate || undefined,
      endDate: form.endDate || undefined,
      deliveryDueDate: form.deliveryDueDate || undefined,
      managerName: form.managerName?.trim() || undefined,
      deliveryPlace: form.deliveryPlace?.trim() || undefined,
      deliveryManagerName: form.deliveryManagerName?.trim() || undefined,
      deliveryManagerPhone: form.deliveryManagerPhone?.trim() || undefined,
      currentStage: form.currentStage || '작성',
      // 저장 시 단계는 재생성되므로 ID보다 단계명이 신뢰됨
      currentStageId: form.currentStageId ?? null,
      stages: (form.stages ?? createDefaultContractStages()).map((stage, index) => ({
        stageName: stage.stageName,
        stageKey: stage.stageKey,
        sortOrder: index,
        boardId: stage.boardId ?? null,
        linkType: stage.linkType ?? null,
      })),
      urgent: Boolean(form.urgent),
      items: payloadItems,
      draft,
    };
  };

  const handleSave = async (draft: boolean) => {
    const payload = buildPayload(draft);
    if (!payload) return;

    try {
      setSaving(true);
      setError('');
      const saved =
        isEdit && contractId != null
          ? await updateContract(contractId, payload)
          : await createContract(payload);
      if (!isEdit) {
        window.alert(`문서번호 ${saved.documentNo || '-'}로 등록되었습니다.`);
      }
      onSaved(saved);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '계약서 저장에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void handleSave(false);
  };

  return (
    <div className="contract-form-page contract-form-page--embedded">
      <header className="contract-form-page__header">
        <div>
          <p className="contract-form-page__eyebrow">CONTRACT</p>
          <h1>{isEdit ? '계약서 수정' : '계약서 등록'}</h1>
          <p>관련 정보를 구역별로 입력한 뒤 저장하세요.</p>
        </div>
        <div className="contract-form-page__header-actions">
          <button type="button" className="contract-form-page__ghost" onClick={onCancel}>
            목록으로
          </button>
        </div>
      </header>

      <div className="contract-form-page__main">
        {loading ? (
          <p className="contract-form-page__hint">불러오는 중...</p>
        ) : (
          <form className="contract-form-page__form" onSubmit={handleSubmit}>
            <ContractStageStepper
              stages={stages}
              currentStageId={form.currentStageId}
              currentStageName={form.currentStage}
              activeIndex={activeStageIndex}
              onSelect={(index) => setActiveStageIndex(index)}
              onAdvance={() => {
                const currentIndex = stages.findIndex(
                  (stage) =>
                    (form.currentStageId != null && stage.stageId === form.currentStageId) ||
                    stage.stageName === form.currentStage
                );
                const from = currentIndex >= 0 ? currentIndex : 0;
                const next = stages[from + 1];
                if (!next) return;
                const ok = window.confirm(`진행 단계를 "${next.stageName}"(으)로 변경할까요?`);
                if (!ok) return;
                setForm((prev) => ({
                  ...prev,
                  currentStage: next.stageName,
                  currentStageId: next.stageId ?? null,
                }));
                setActiveStageIndex(from + 1);
              }}
              onRetreat={() => {
                const currentIndex = stages.findIndex(
                  (stage) =>
                    (form.currentStageId != null && stage.stageId === form.currentStageId) ||
                    stage.stageName === form.currentStage
                );
                const from = currentIndex >= 0 ? currentIndex : 0;
                const prevStage = stages[from - 1];
                if (!prevStage) return;
                const ok = window.confirm(
                  `진행 단계를 "${prevStage.stageName}"(으)로 되돌릴까요?`
                );
                if (!ok) return;
                setForm((prev) => ({
                  ...prev,
                  currentStage: prevStage.stageName,
                  currentStageId: prevStage.stageId ?? null,
                }));
                setActiveStageIndex(from - 1);
              }}
              showEditButton
              onEditStages={() => setEditingStages((prev) => !prev)}
            />

            {editingStages && (
              <ContractStageEditor
                stages={stages}
                boards={boards}
                onChange={(nextStages: ContractStage[]) => {
                  setForm((prev) => ({ ...prev, stages: nextStages }));
                  setActiveStageIndex((prev) => Math.min(prev, Math.max(0, nextStages.length - 1)));
                }}
                onClose={() => setEditingStages(false)}
              />
            )}

            {!showWritingBody ? (
              <ContractStageEmbed
                stage={activeStage}
                contractId={isEdit ? contractId : null}
              />
            ) : (
              <>
            <section className="contract-form-page__card">
              <div className="contract-form-page__card-head">
                <h2>계약 정보</h2>
                <span>번호 · 기간 · 긴급여부</span>
              </div>
              <div className="contract-form-page__grid">
                <label>
                  문서번호
                  <input
                    value={isEdit ? documentNo : ''}
                    readOnly
                    maxLength={100}
                    placeholder={isEdit ? undefined : '저장 시 자동 발급'}
                  />
                </label>
                <label>
                  계약번호
                  <input
                    value={form.contractNo ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, contractNo: e.target.value }))}
                    maxLength={100}
                    placeholder="실제 계약번호 (선택)"
                  />
                </label>
                <label>
                  계약명
                  <input
                    value={form.contractName}
                    onChange={(e) => setForm((prev) => ({ ...prev, contractName: e.target.value }))}
                    maxLength={255}
                  />
                </label>
                <label>
                  계약 시작일
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
                  />
                </label>
                <label>
                  계약 종료일
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
                  />
                </label>
                <label className="contract-form-page__check">
                  <input
                    type="checkbox"
                    checked={Boolean(form.urgent)}
                    onChange={(e) => setForm((prev) => ({ ...prev, urgent: e.target.checked }))}
                  />
                  긴급 계약
                </label>
              </div>
            </section>

            <section className="contract-form-page__card">
              <div className="contract-form-page__card-head">
                <h2>발주처</h2>
                <span>고객사 및 담당자</span>
              </div>
              <div className="contract-form-page__grid">
                <label className="is-span-2">
                  발주처
                  <input
                    value={form.clientName}
                    onChange={(e) => setForm((prev) => ({ ...prev, clientName: e.target.value }))}
                    maxLength={255}
                  />
                </label>
                <label>
                  발주처 담당자
                  <input
                    value={form.clientManagerName}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, clientManagerName: e.target.value }))
                    }
                    maxLength={100}
                  />
                </label>
                <label>
                  발주처 담당자 연락처
                  <input
                    value={form.clientManagerPhone}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, clientManagerPhone: e.target.value }))
                    }
                    maxLength={50}
                  />
                </label>
              </div>
            </section>

            <div className="contract-form-page__two-col">
              <section className="contract-form-page__card">
                <div className="contract-form-page__card-head">
                  <h2>사내 담당</h2>
                  <span>내부 관리 정보</span>
                </div>
                <div className="contract-form-page__grid is-single">
                  <label>
                    사내 담당자
                    <input
                      value={form.managerName}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, managerName: e.target.value }))
                      }
                      maxLength={100}
                    />
                  </label>
                </div>
              </section>

              <section className="contract-form-page__card">
                <div className="contract-form-page__card-head">
                  <h2>납품 정보</h2>
                  <span>장소 · 일정 · 담당자</span>
                </div>
                <div className="contract-form-page__grid">
                  <label className="is-span-2">
                    납품장소
                    <input
                      value={form.deliveryPlace}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, deliveryPlace: e.target.value }))
                      }
                      maxLength={255}
                    />
                  </label>
                  <label>
                    납품 예정일
                    <input
                      type="date"
                      value={form.deliveryDueDate}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, deliveryDueDate: e.target.value }))
                      }
                    />
                  </label>
                  <label>
                    납품담당자
                    <input
                      value={form.deliveryManagerName}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, deliveryManagerName: e.target.value }))
                      }
                      maxLength={100}
                    />
                  </label>
                  <label className="is-span-2">
                    납품담당자 연락처
                    <input
                      value={form.deliveryManagerPhone}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, deliveryManagerPhone: e.target.value }))
                      }
                      maxLength={50}
                    />
                  </label>
                </div>
              </section>
            </div>

            <section className="contract-form-page__card">
              <div className="contract-form-page__items-header">
                <div className="contract-form-page__card-head">
                  <h2>제품 내역</h2>
                  <span>회사 제품 선택 · 없으면 팝업에서 빠른 등록</span>
                </div>
                <button type="button" className="contract-form-page__ghost" onClick={addItem}>
                  + 행 추가
                </button>
              </div>
              <p className="contract-form-page__items-hint">
                제품 선택 팝업에서 고르면 단위·공급단가가 채워집니다. 목록에 없으면 빠른 등록으로
                추가할 수 있습니다.
              </p>
              <div className="contract-form-page__table-wrap">
                <table className="contract-form-page__items-table">
                  <thead>
                    <tr>
                      <th style={{ width: '48px' }}>No</th>
                      <th>제품명</th>
                      <th style={{ width: '90px' }}>단위</th>
                      <th style={{ width: '90px' }}>수량</th>
                      <th style={{ width: '120px' }}>단가</th>
                      <th style={{ width: '120px' }}>금액</th>
                      <th style={{ width: '64px' }}>삭제</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={`item-${index}`}>
                        <td>{index + 1}</td>
                        <td>
                          <ContractProductCell
                            productId={item.productId}
                            productName={item.productName}
                            products={products}
                            onProductsUpdated={setProducts}
                            onSelectProduct={(product) => applyProductToItem(index, product)}
                            onClear={() =>
                              updateItem(index, {
                                productId: null,
                                productName: '',
                                unit: 'EA',
                                unitPrice: null,
                              })
                            }
                          />
                        </td>
                        <td>
                          <select
                            value={item.unit || 'EA'}
                            onChange={(e) => updateItem(index, { unit: e.target.value })}
                          >
                            {CONTRACT_UNITS.map((unit) => (
                              <option key={unit} value={unit}>
                                {unit}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={formatNumberDisplay(item.quantity)}
                            onChange={(e) => {
                              const parsed = parseNumberInput(formatNumberInput(e.target.value));
                              updateItem(index, {
                                quantity: parsed == null || parsed < 1 ? 1 : Math.floor(parsed) || 1,
                              });
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatUnitPriceDisplay(item.unitPrice)}
                            placeholder="0"
                            onChange={(e) => {
                              const formatted = formatUnitPriceInput(e.target.value);
                              const parsed = parseUnitPriceInput(formatted);
                              updateItem(index, {
                                unitPrice: parsed == null || parsed < 0 ? null : parsed,
                              });
                            }}
                          />
                        </td>
                        <td className="contract-form-page__amount">
                          {formatWon(calcItemAmount(item))}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="contract-form-page__row-remove"
                            onClick={() => removeItem(index)}
                            title="행 삭제"
                          >
                            −
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} className="contract-form-page__total-label">
                        합계
                      </td>
                      <td className="contract-form-page__amount">{formatWon(totalAmount)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>

            <div className="contract-form-page__comments">
              <ContractComments
                contractId={isEdit ? contractId : null}
                enabled={isEdit && contractId != null}
              />
            </div>
              </>
            )}

            {error && <p className="contract-form-page__error">{error}</p>}

            <div className="contract-form-page__actions">
              <button
                type="button"
                className="contract-form-page__ghost"
                disabled={saving}
                onClick={() => void handleSave(true)}
              >
                {saving ? '저장 중...' : '임시저장'}
              </button>
              <button type="submit" className="contract-form-page__primary" disabled={saving}>
                {saving ? '저장 중...' : isEdit ? '등록 저장' : '등록'}
              </button>
              <button type="button" className="contract-form-page__ghost" onClick={onCancel}>
                취소
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function formatWon(value: number) {
  return `${value.toLocaleString('ko-KR')}원`;
}

export default ContractForm;
